// src/pages/DevicesPage.jsx — IMA Device Inventory
// Pulls from NinjaRMM, Intune (Graph), CrowdStrike
// Stores manual overrides (status, location, notes) in SharePoint ITTechs list
import React, { useState, useEffect } from "react";
import { graphRequest, FUNCTION_APP_URL, FUNCTION_KEY } from "../services/graphService";
import { GRAPH_BASE } from "../authConfig";
import { Spinner, Btn, Badge, PageHeader, StatGrid, Card } from "../components/UI";

// ── Config ─────────────────────────────────────────────────────────────────────
// API calls go through Azure Functions — matches existing portal pattern
const SP_SITE_PATH = "imagroupmgmt.sharepoint.com:/sites/ITTechs";
const SP_LIST_NAME = "Device Management";

// ── IMA Office Locations ───────────────────────────────────────────────────────
const OFFICES = [
  // CR sites (from imaresearch.com)
  { id:"cr-albany-ny",          label:"CR — Albany, NY (1762 Central Ave)",           org:"CR", city:"Albany, NY" },
  { id:"cr-albuquerque-nm",     label:"CR — Albuquerque, NM (101 Hospital Loop NE)",  org:"CR", city:"Albuquerque, NM" },
  { id:"cr-austin-northland-tx",label:"CR — Austin, TX (3307 Northland Dr)",          org:"CR", city:"Austin, TX" },
  { id:"cr-austin-cannon-tx",   label:"CR — Austin, TX (111 W William Cannon Dr)",    org:"CR", city:"Austin, TX" },
  { id:"cr-chicago-il",         label:"CR — Chicago, IL (3401 N Central Ave)",        org:"CR", city:"Chicago, IL" },
  { id:"cr-hartsdale-ny",       label:"CR — Hartsdale, NY",                           org:"CR", city:"Hartsdale, NY" },
  { id:"cr-las-vegas-nv",       label:"CR — Las Vegas, NV (3012 W Charleston Blvd)",  org:"CR", city:"Las Vegas, NV" },
  { id:"cr-lenoir-nc",          label:"CR — Lenoir, NC (224 Sharon Ave)",             org:"CR", city:"Lenoir, NC" },
  { id:"cr-monroe-la-18th",     label:"CR — Monroe, LA (18th St)",                   org:"CR", city:"Monroe, LA" },
  { id:"cr-monroe-la-wood",     label:"CR — Monroe, LA (Wood St)",                   org:"CR", city:"Monroe, LA" },
  { id:"cr-mount-airy-nc",      label:"CR — Mount Airy, NC (911 Worth St)",           org:"CR", city:"Mount Airy, NC" },
  { id:"cr-new-york-midtown",   label:"CR — Manhattan, NY (Midtown — 33 W 46th St)", org:"CR", city:"New York, NY" },
  { id:"cr-new-york-uptown",    label:"CR — Manhattan, NY (Uptown — 134 E 93rd St)", org:"CR", city:"New York, NY" },
  { id:"cr-phoenix-az",         label:"CR — Phoenix, AZ (3030 N Central Ave)",        org:"CR", city:"Phoenix, AZ" },
  { id:"cr-san-antonio-tx",     label:"CR — San Antonio, TX (4410 Medical Dr)",       org:"CR", city:"San Antonio, TX" },
  { id:"cr-st-pete-fl",         label:"CR — St. Petersburg, FL (1530 4th St N)",      org:"CR", city:"St. Petersburg, FL" },
  { id:"cr-warren-nj",          label:"CR — Warren, NJ (10 Independence Blvd)",       org:"CR", city:"Warren, NJ" },
  // IMA Gov Services sites (from theimagroup.com)
  { id:"ima-albany-ny",         label:"IMA — Albany, NY (1762 Central Ave)",          org:"IMA", city:"Albany, NY" },
  { id:"ima-allentown-pa",      label:"IMA — Allentown, PA",                          org:"IMA", city:"Allentown, PA" },
  { id:"ima-altoona-pa",        label:"IMA — Altoona, PA",                            org:"IMA", city:"Altoona, PA" },
  { id:"ima-baltimore-md",      label:"IMA — Baltimore, MD",                          org:"IMA", city:"Baltimore, MD" },
  { id:"ima-baton-rouge-la",    label:"IMA — Baton Rouge, LA",                        org:"IMA", city:"Baton Rouge, LA" },
  { id:"ima-beckley-wv",        label:"IMA — Beckley, WV",                            org:"IMA", city:"Beckley, WV" },
  { id:"ima-bohemia-ny",        label:"IMA — Bohemia, NY (1075 Portion Rd)",          org:"IMA", city:"Bohemia, NY" },
  { id:"ima-bronx-ny",          label:"IMA — Bronx, NY (3250 Westchester Ave)",       org:"IMA", city:"Bronx, NY" },
  { id:"ima-brooklyn-ny",       label:"IMA — Brooklyn, NY (50 Court St)",             org:"IMA", city:"Brooklyn, NY" },
  { id:"ima-buffalo-ny",        label:"IMA — Buffalo, NY (699 Hertel Ave)",           org:"IMA", city:"Buffalo, NY" },
  { id:"ima-chicago-il",        label:"IMA — Chicago, IL",                            org:"IMA", city:"Chicago, IL" },
  { id:"ima-cincinnati-oh",     label:"IMA — Cincinnati, OH",                         org:"IMA", city:"Cincinnati, OH" },
  { id:"ima-clarion-pa",        label:"IMA — Clarion, PA",                            org:"IMA", city:"Clarion, PA" },
  { id:"ima-columbus-oh",       label:"IMA — Columbus, OH",                           org:"IMA", city:"Columbus, OH" },
  { id:"ima-dallas-tx",         label:"IMA — Dallas, TX",                             org:"IMA", city:"Dallas, TX" },
  { id:"ima-dover-de",          label:"IMA — Dover, DE",                              org:"IMA", city:"Dover, DE" },
  { id:"ima-elmira-ny",         label:"IMA — Elmira, NY (1300 College Ave)",          org:"IMA", city:"Elmira, NY" },
  { id:"ima-endicott-ny",       label:"IMA — Endicott, NY (609 E Main St)",           org:"IMA", city:"Endicott, NY" },
  { id:"ima-erie-pa",           label:"IMA — Erie, PA",                               org:"IMA", city:"Erie, PA" },
  { id:"ima-fort-myers-fl",     label:"IMA — Fort Myers, FL",                         org:"IMA", city:"Fort Myers, FL" },
  { id:"ima-fort-worth-tx",     label:"IMA — Fort Worth, TX",                         org:"IMA", city:"Fort Worth, TX" },
  { id:"ima-forty-fort-pa",     label:"IMA — Forty Fort, PA",                         org:"IMA", city:"Forty Fort, PA" },
  { id:"ima-hampton-va",        label:"IMA — Hampton, VA",                            org:"IMA", city:"Hampton, VA" },
  { id:"ima-hartsdale-ny",      label:"IMA — Hartsdale, NY (220 N Central Ave)",      org:"IMA", city:"Hartsdale, NY" },
  { id:"ima-hempstead-ny",      label:"IMA — Hempstead, NY",                          org:"IMA", city:"Hempstead, NY" },
  { id:"ima-hialeah-fl",        label:"IMA — Hialeah, FL",                            org:"IMA", city:"Hialeah, FL" },
  { id:"ima-hickory-nc",        label:"IMA — Hickory, NC",                            org:"IMA", city:"Hickory, NC" },
  { id:"ima-houston-tx",        label:"IMA — Houston, TX",                            org:"IMA", city:"Houston, TX" },
  { id:"ima-huntington-wv",     label:"IMA — Huntington, WV",                         org:"IMA", city:"Huntington, WV" },
  { id:"ima-huntsville-al",     label:"IMA — Huntsville, AL",                         org:"IMA", city:"Huntsville, AL" },
  { id:"ima-jacksonville-fl",   label:"IMA — Jacksonville, FL",                       org:"IMA", city:"Jacksonville, FL" },
  { id:"ima-jamestown-ny",      label:"IMA — Jamestown, NY",                          org:"IMA", city:"Jamestown, NY" },
  { id:"ima-kew-gardens-ny",    label:"IMA — Kew Gardens, NY",                        org:"IMA", city:"Kew Gardens, NY" },
  { id:"ima-las-vegas-nv",      label:"IMA — Las Vegas, NV",                          org:"IMA", city:"Las Vegas, NV" },
  { id:"ima-lenoir-nc",         label:"IMA — Lenoir, NC",                             org:"IMA", city:"Lenoir, NC" },
  { id:"ima-little-rock-ar",    label:"IMA — Little Rock, AR",                        org:"IMA", city:"Little Rock, AR" },
  { id:"ima-louisville-ky",     label:"IMA — Louisville, KY",                         org:"IMA", city:"Louisville, KY" },
  { id:"ima-mechanicsburg-pa",  label:"IMA — Mechanicsburg, PA",                      org:"IMA", city:"Mechanicsburg, PA" },
  { id:"ima-middleburg-hts-oh", label:"IMA — Middleburg Heights, OH",                 org:"IMA", city:"Middleburg Heights, OH" },
  { id:"ima-middletown-ny",     label:"IMA — Middletown, NY",                         org:"IMA", city:"Middletown, NY" },
  { id:"ima-monroe-la",         label:"IMA — Monroe, LA",                             org:"IMA", city:"Monroe, LA" },
  { id:"ima-morgantown-wv",     label:"IMA — Morgantown, WV",                         org:"IMA", city:"Morgantown, WV" },
  { id:"ima-mount-airy-nc",     label:"IMA — Mount Airy, NC",                         org:"IMA", city:"Mount Airy, NC" },
  { id:"ima-new-castle-pa",     label:"IMA — New Castle, PA",                         org:"IMA", city:"New Castle, PA" },
  { id:"ima-new-york-ny",       label:"IMA — New York, NY (Gov Services)",            org:"IMA", city:"New York, NY" },
  { id:"ima-palm-bay-fl",       label:"IMA — Palm Bay, FL",                           org:"IMA", city:"Palm Bay, FL" },
  { id:"ima-panama-city-fl",    label:"IMA — Panama City, FL",                        org:"IMA", city:"Panama City, FL" },
  { id:"ima-philadelphia-pa",   label:"IMA — Philadelphia, PA",                       org:"IMA", city:"Philadelphia, PA" },
  { id:"ima-phoenix-az",        label:"IMA — Phoenix, AZ (Gov Services)",             org:"IMA", city:"Phoenix, AZ" },
  { id:"ima-pittsburgh-pa",     label:"IMA — Pittsburgh, PA",                         org:"IMA", city:"Pittsburgh, PA" },
  { id:"ima-plattsburgh-ny",    label:"IMA — Plattsburgh, NY",                        org:"IMA", city:"Plattsburgh, NY" },
  { id:"ima-poughkeepsie-ny",   label:"IMA — Poughkeepsie, NY",                       org:"IMA", city:"Poughkeepsie, NY" },
  { id:"ima-rochester-ny",      label:"IMA — Rochester, NY",                          org:"IMA", city:"Rochester, NY" },
  { id:"ima-san-antonio-tx",    label:"IMA — San Antonio, TX (Gov Services)",         org:"IMA", city:"San Antonio, TX" },
  { id:"ima-southfield-mi",     label:"IMA — Southfield, MI",                         org:"IMA", city:"Southfield, MI" },
  { id:"ima-st-louis-mo",       label:"IMA — St. Louis, MO",                          org:"IMA", city:"St. Louis, MO" },
  { id:"ima-st-pete-fl",        label:"IMA — St. Petersburg, FL (Gov Services)",      org:"IMA", city:"St. Petersburg, FL" },
  { id:"ima-staten-island-ny",  label:"IMA — Staten Island, NY",                      org:"IMA", city:"Staten Island, NY" },
  { id:"ima-stratford-ct",      label:"IMA — Stratford, CT",                          org:"IMA", city:"Stratford, CT" },
  { id:"ima-syracuse-ny",       label:"IMA — Syracuse, NY",                           org:"IMA", city:"Syracuse, NY" },
  { id:"ima-tampa-fl",          label:"IMA — Tampa, FL",                              org:"IMA", city:"Tampa, FL" },
  { id:"ima-toledo-oh",         label:"IMA — Toledo, OH",                             org:"IMA", city:"Toledo, OH" },
  { id:"ima-trenton-nj",        label:"IMA — Trenton, NJ",                            org:"IMA", city:"Trenton, NJ" },
  { id:"ima-troy-ny",           label:"IMA — Troy, NY",                               org:"IMA", city:"Troy, NY" },
  { id:"ima-tucson-az",         label:"IMA — Tucson, AZ",                             org:"IMA", city:"Tucson, AZ" },
  { id:"ima-utica-ny",          label:"IMA — Utica, NY",                              org:"IMA", city:"Utica, NY" },
  { id:"ima-virginia-beach-va", label:"IMA — Virginia Beach, VA",                     org:"IMA", city:"Virginia Beach, VA" },
  { id:"ima-watertown-ny",      label:"IMA — Watertown, NY",                          org:"IMA", city:"Watertown, NY" },
  { id:"ima-west-palm-beach-fl",label:"IMA — West Palm Beach, FL",                    org:"IMA", city:"West Palm Beach, FL" },
  { id:"ima-white-plains-ny",   label:"IMA — White Plains, NY",                       org:"IMA", city:"White Plains, NY" },
  { id:"ima-wilkes-barre-pa",   label:"IMA — Wilkes-Barre, PA",                       org:"IMA", city:"Wilkes-Barre, PA" },
  { id:"ima-wilmington-de",     label:"IMA — Wilmington, DE",                         org:"IMA", city:"Wilmington, DE" },
  { id:"corp-remote",           label:"Corporate — Remote",                           org:"Corporate", city:"Remote" },
  { id:"payerservices-rmt",    label:"Payer Services — Remote",                     org:"PayerServices", city:"Remote" },
  { id:"corp-tarrytown-hq",     label:"Corporate HQ — Tarrytown, NY",               org:"Corporate", city:"Tarrytown, NY" },
  { id:"payer-syracuse-ny",     label:"Payer Services — Syracuse, NY",               org:"PayerServices", city:"Syracuse, NY" },
  { id:"remote",                label:"Remote / Work From Home",                     org:"",    city:"Remote" },
  { id:"unknown",               label:"Unknown / Unassigned",                        org:"",    city:"" },
];

const OFFICE_BY_ID = Object.fromEntries(OFFICES.map(o => [o.id, o]));

const PREFIX_MAP = {
  NYALB:"ima-albany-ny",    NYBRX:"ima-bronx-ny",       NYBOH:"ima-bohemia-ny",
  NYBUF:"ima-buffalo-ny",   NYNYC:"ima-new-york-ny",    NYRMT:"remote",
  NYROC:"ima-rochester-ny", NYSYR:"ima-syracuse-ny",    NYTTN:"ima-troy-ny",
  NYUMC:"cr-new-york-midtown", NYHOL:"ima-hempstead-ny",NYHEM:"ima-hempstead-ny",
  NYJAM:"ima-jamestown-ny", NYMID:"ima-middletown-ny",  NYMRN:"ima-monroe-la",
  NYQUE:"ima-kew-gardens-ny",
  NYTRY:"corp-tarrytown-hq",   NYCOR:"corp-tarrytown-hq",
  ILCHI:"ima-chicago-il",   ILCRC:"cr-chicago-il",      ILRMT:"remote",
  PAALL:"ima-allentown-pa", PAALT:"ima-altoona-pa",     PACLA:"ima-clarion-pa",
  PAERI:"ima-erie-pa",      PAFOR:"ima-forty-fort-pa",  PAMEC:"ima-mechanicsburg-pa",
  PANEW:"ima-new-castle-pa",PAPHL:"ima-philadelphia-pa",PAPIL:"ima-pittsburgh-pa",
  PAPIT:"ima-pittsburgh-pa",PARMT:"remote",             PAWIL:"ima-wilkes-barre-pa",
  TXAUS:"cr-austin-cannon-tx",TXFTW:"ima-fort-worth-tx",TXRMT:"remote",
  TXSAN:"ima-san-antonio-tx",
  NVLAS:"ima-las-vegas-nv", NVRMT:"remote",
  AZPHX:"ima-phoenix-az",   AZRMT:"remote",
  FLRMT:"remote",           FLSTP:"ima-st-pete-fl",     FLWPB:"ima-west-palm-beach-fl",
  FLAVE:"ima-virginia-beach-va",
  OHCIN:"ima-cincinnati-oh",OHCOL:"ima-columbus-oh",    OHRMT:"remote",
  ORRMT:"remote",           MDRMT:"remote",             MIRMT:"remote",
  MIDET:"ima-southfield-mi",MOFPK:"ima-st-louis-mo",   NCRMT:"remote",
  NCMTA:"ima-mount-airy-nc",NJRMT:"remote",             NJWAR:"ima-trenton-nj",
  INRMT:"remote",           TNRMT:"remote",             UTRMT:"remote",
  CARMT:"remote",           DEDOV:"ima-dover-de",       DCWAS:"remote",
  WARMT:"remote",           ORCHM:"cr-chicago-il",      PSYBA:"corp-remote",
  PAYRS:"payerservices-rmt",
};

const DEVICE_STATUSES = [
  "Deployed",
  "Ready for Deployment",
  "Under Maintenance",
  "Spare / In Storage",
  "Retired",
  "Lost / Stolen",
];

const STATUS_COLORS = {
  Deployed:             "var(--ima-green)",
  Active:               "var(--ima-blue)",
  "Ready for Deployment":"var(--purple)",
  "Under Maintenance":  "var(--ima-accent)",
  Inactive:             "var(--red)",
  Retired:              "var(--tx3)",
  "Lost / Stolen":      "var(--red)",
};

const NINJA_STATUS_COLORS = {
  Active:  "var(--ima-blue)",
  Stale:   "var(--ima-accent)",
  Unknown: "var(--tx3)",
};

// ── SharePoint helpers (uses MSAL graphRequest — user token) ──────────────────
let spSiteId = null;
let spListId = null;

async function ensureSPList() {
  if (spSiteId && spListId) return;
  const site = await graphRequest(`${GRAPH_BASE}/sites/${SP_SITE_PATH}`);
  spSiteId = site.id;
  const lists = await graphRequest(
    `${GRAPH_BASE}/sites/${spSiteId}/lists?$filter=displayName eq '${SP_LIST_NAME}'&$select=id`
  );
  if (lists.value?.length) {
    spListId = lists.value[0].id;
  } else {
    throw new Error(`SharePoint list "${SP_LIST_NAME}" not found. Check the list exists on the ITTechs site.`);
  }
}

async function loadSPOverrides() {
  await ensureSPList();
  const overrides = {};
  // Don't $select specific fields - let SP return all, then read by display name via column map
  let url = `${GRAPH_BASE}/sites/${spSiteId}/lists/${spListId}/items?$expand=fields&$top=999`;
  while (url) {
    const resp = await graphRequest(url);
    for (const item of (resp.value || [])) {
      const f = item.fields;
      const key = (f.DeviceHostname || "").toUpperCase();
      const serialKey = (f.SerialNumber || "").toUpperCase();
      // Try both internal name variants (SP sometimes appends 0 to avoid conflicts)
      const rec = {
        spItemId:     item.id,
        deviceStatus: f.Title         || "",   // DeviceStatus stored in Title field
        officeId:     f.OfficeID      || "",   // capital D
        location:     f.LocationLabel || "",
        assignedUser: f.AssignedUser  || "",
        assetTag:     f.AssetTag      || "",
        notes:        f.Notes         || "",
        serialNumber: f.SerialNumber  || "",
      };
      if (key) overrides[key] = rec;
      // Also index by serial so renames don't lose saved data
      if (serialKey && !["NONE","DEFAULTSTRING","0",""].includes(serialKey)) overrides["SN:" + serialKey] = rec;
    }
    url = resp["@odata.nextLink"] || null;
  }
  return overrides;
}

// Hardcoded SP internal field names (confirmed from column map inspection)
// DeviceStatus display name maps to "Title" (SP's built-in title field)
// OfficeID has capital D
const SP_FIELD_MAP = {
  DeviceHostname: "DeviceHostname",
  DeviceStatus:   "Title",        // SP maps DeviceStatus column to Title/LinkTitle
  OfficeId:       "OfficeID",     // capital D
  OfficeID:       "OfficeID",
  LocationLabel:  "LocationLabel",
  AssignedUser:   "AssignedUser",
  AssetTag:       "AssetTag",
  Notes:          "Notes",
  UpdatedAt:      "UpdatedAt",
  SerialNumber:   "SerialNumber",
};

// SP built-in read-only fields to skip
const SP_READONLY = new Set([
  "LinkTitle","LinkTitleNoMenu","Title","ID","id","Modified","Created",
  "Author","Editor","_UIVersionString","ContentType","_ModerationStatus",
  "CheckoutUser","InstanceID","Order","GUID","WorkflowVersion",
  "WorkflowInstanceID","ParentVersionString","ParentLeafName",
  "DocIcon","Edit","SelectTitle","SelectFilename","ItemChildCount",
  "FolderChildCount","SMTotalSize","SMLastModifiedDate","SMTotalFileStreamSize",
  "SMTotalFileCount","owshiddenversion","UniqueId","SyncClientId","ProgId",
  "ScopeId","HTML_x0020_File_x0020_Type","_EditMenuTableStart",
  "_EditMenuTableStart2","_EditMenuTableEnd","LinkCheckedOutTitle",
  "_CheckinComment","FileLeafRef","FileRef","FSObjType","SortBehavior",
  "PermMask","_HasCopyDestinations","_CopySource","ContentTypeId",
  "Attachments","_ModerationComments","File_x0020_Type","MetaInfo",
  "_Level","_IsCurrentVersion","ItemChildCount","AppAuthor","AppEditor",
]);

// Map display-name-keyed fields to internal SP field names
async function mapFields(fields) {
  const colMap = await getSPColumnMap();
  const mapped = {};
  Object.entries(fields).forEach(([displayName, val]) => {
    const internalName = colMap[displayName] || displayName;
    // Skip any field that maps to a known read-only system field
    if (SP_READONLY.has(internalName)) return;
    mapped[internalName] = val;
  });
  return mapped;
}

async function saveSPItem(hostname, fields, spItemId) {
  await ensureSPList();
  // Map to confirmed SP internal field names
  const mapped = {};
  Object.entries(fields).forEach(([key, val]) => {
    const internalName = SP_FIELD_MAP[key] || key;
    mapped[internalName] = val;
  });
  if (spItemId) {
    await graphRequest(`${GRAPH_BASE}/sites/${spSiteId}/lists/${spListId}/items/${spItemId}/fields`, {
      method: "PATCH", body: JSON.stringify(mapped),
    });
    return spItemId;
  } else {
    const created = await graphRequest(`${GRAPH_BASE}/sites/${spSiteId}/lists/${spListId}/items`, {
      method: "POST", body: JSON.stringify({ fields: mapped }),
    });
    return created.id;
  }
}

// ── Device categorisation ──────────────────────────────────────────────────────
function getCategory(os, mfr, model, hostname) {
  const o=(os||"").toLowerCase(), h=(hostname||"").toLowerCase(),
        mo=(model||"").toLowerCase(), mf=(mfr||"").toLowerCase();
  if (o.includes("linux")) return "Linux";
  if (o.includes("windows server")||h.match(/^ima-az-/)||h.match(/-dc\d*$|-rds\d*$|-sql\d*$/)||mf.startsWith("vmware")||mo.includes("vmware")) return "Server";
  if (o.includes("mac")) return "Mac";
  return "Windows";
}

function guessOfficeId(hostname) {
  if (!hostname) return "unknown";
  return PREFIX_MAP[hostname.substring(0,5).toUpperCase()] || "unknown";
}

// ── Reconciliation ─────────────────────────────────────────────────────────────
function reconcile(ninjaDevices, intuneDevices, csDevices, overrides, ninjaOrgMap={}, ninjaLocMap={}) {
  const intuneBySerial = {}, intuneByHost = {};
  intuneDevices.forEach(d => {
    if (d.managedDeviceOwnerType === "personal") return;
    const s = (d.serialNumber||"").trim().toUpperCase();
    const h = (d.deviceName||"").toLowerCase();
    if (s && !["NONE","DEFAULTSTRING","0","00000000","SYSTEMSERIALNUMBER"].includes(s)) intuneBySerial[s] = d;
    if (h) intuneByHost[h] = d;
  });

  const csBySerial = {}, csByHost = {};
  csDevices.forEach(d => {
    const s = (d.serial_number||"").trim().toUpperCase();
    const h = (d.hostname||"").toLowerCase();
    if (s && !["NONE","0",""].includes(s)) csBySerial[s] = d;
    if (h) csByHost[h] = d;
  });

  const usedIntune = new Set();
  const result = [];
  const seenKeys = {}; // dedup by serial or hostname

  ninjaDevices.forEach(n => {
    const serial   = (n.system?.biosSerialNumber||n.system?.serialNumber||"").trim().toUpperCase();
    const hostname = n.systemName||n.dnsName||"";
    const hLower   = hostname.toLowerCase();
    const lastNinja = n.lastContact ? new Date(n.lastContact*1000) : null;
    const i  = (serial && intuneBySerial[serial]) || intuneByHost[hLower];
    const cs = (serial && csBySerial[serial]) || csByHost[hLower];
    if (i) usedIntune.add(i.id);
    const lastIntune = i?.lastSyncDateTime ? new Date(i.lastSyncDateTime) : null;
    const lastCS     = cs?.last_seen ? new Date(cs.last_seen) : null;
    const lastSeen   = [lastNinja,lastIntune,lastCS].filter(Boolean).sort((a,b)=>b-a)[0];
    const days       = lastSeen ? Math.floor((Date.now()-lastSeen)/86400000) : 9999;
    // Deduplicate — serial is the canonical key, hostname as fallback
    const dk = serial || hLower;
    if (dk && seenKeys[dk]) {
      const prev = seenKeys[dk];
      const prevDate = prev.lastSeen ? new Date(prev.lastSeen) : new Date(0);
      if (lastSeen && lastSeen > prevDate) {
        result.splice(result.indexOf(prev), 1); // replace with newer
      } else {
        return; // existing is newer, skip this one
      }
    }
    const ninjaLoc   = ninjaLocMap[n.locationId]   || {};
    const ninjaOrgN  = ninjaOrgMap[n.organizationId] || ninjaLoc.orgName || "";
    const rec = buildRecord({
      hostname, serial, os:n.os?.name, osBuild:n.os?.buildNumber,
      mfr:n.system?.manufacturer, model:n.system?.model,
      ip:n.ipAddresses?.[0],
      ninjaUser:  n.lastLoggedInUser || "",          // most accurate
      intuneUPN:  i?.userPrincipalName || "",        // enrolled user UPN fallback
      user:       i?.userDisplayName || "",           // Intune display name (may be IT person)
      csUser:     cs?.current_local_administrator || "", // CS fallback
      lastSeen, days, inNinja:true, inIntune:!!i, inCS:!!cs,
      compliance:i?.complianceState, csVersion:cs?.agent_version,
      ninjaLocationId:   n.locationId,
      ninjaLocationName: ninjaLoc.name    || "",
      ninjaOrgName:      ninjaOrgN,
      category:          getCategory(n.os?.name, n.system?.manufacturer, n.system?.model, hostname),
    }, overrides);
    if (dk) seenKeys[dk] = rec;
    result.push(rec);
  });

  intuneDevices.forEach(d => {
    if (d.managedDeviceOwnerType==="personal" || usedIntune.has(d.id)) return;
    const serial   = (d.serialNumber||"").toUpperCase();
    const hostname = d.deviceName||"";
    const cat      = getCategory(d.operatingSystem, d.manufacturer, d.model, hostname);
    const cs = (serial && csBySerial[serial]) || csByHost[hostname.toLowerCase()];
    const lastSeen = d.lastSyncDateTime ? new Date(d.lastSyncDateTime) : null;
    const days     = lastSeen ? Math.floor((Date.now()-lastSeen)/86400000) : 9999;
    result.push(buildRecord({ hostname, serial, os:d.operatingSystem, osBuild:d.osVersion, mfr:d.manufacturer, model:d.model, intuneUPN:d.userPrincipalName||"", user:d.userDisplayName||"", lastSeen, days, inNinja:false, inIntune:true, inCS:!!cs, compliance:d.complianceState, csVersion:cs?.agent_version, category: getCategory(d.operatingSystem, d.manufacturer, d.model, hostname) }, overrides));
  });

  return result;
}

function buildRecord(p, overrides={}) {
  const serialKey = p.serial ? "SN:" + p.serial.toUpperCase() : null;
  const m = overrides[(p.hostname||"").toUpperCase()]
         || (serialKey ? overrides[serialKey] : null)
         || {};
  const ninjaStatus = p.days<=30?"Active":p.days<=60?"Stale":"Unknown";

  // Location priority: SP override > Ninja location name > hostname prefix guess
  let officeId = m.officeId || null;
  let locationLabel = m.location || null;
  let locationOrg   = "";

  if (!officeId && p.ninjaLocationName) {
    // Use Ninja location/org directly as display label
    locationLabel = p.ninjaOrgName
      ? `${p.ninjaOrgName} — ${p.ninjaLocationName}`
      : p.ninjaLocationName;
    locationOrg = p.ninjaOrgName || "";
    officeId = "ninja:" + (p.ninjaLocationId || "");
  }

  if (!officeId) {
    officeId = guessOfficeId(p.hostname);
  }

  const office = OFFICE_BY_ID[officeId];
  if (!locationLabel) {
    locationLabel = office?.label || (p.ninjaOrgName ? `${p.ninjaOrgName}` : "Unknown / Unassigned");
  }
  if (!locationOrg && office) locationOrg = office.org || "";

  return {
    hostname:     p.hostname||"",
    serial:       p.serial||"",
    deviceCategory: p.category||"Windows",
    ninjaStatus,
    deviceStatus: m.deviceStatus || (p.inNinja&&p.inIntune ? "Deployed" : "Ready for Deployment"),
    officeId,
    location:     locationLabel,
    locationOrg,
    ninjaOrg:     p.ninjaOrgName||"",
    ninjaLocation:p.ninjaLocationName||"",
    user:         m.assignedUser || p.ninjaUser || p.csUser || p.intuneUPN || p.user || "",
    assetTag:     m.assetTag||"",  notes: m.notes||"",
    lastSeen:     p.lastSeen ? p.lastSeen.toISOString().split("T")[0] : "",
    os:p.os||"", mfr:p.mfr||"", model:p.model||"", ip:p.ip||"",
    compliance:   p.compliance||"",
    inNinja:      !!p.inNinja, inIntune: !!p.inIntune, inCS: !!p.inCS,
    csVersion:    p.csVersion||"",
    spItemId:     m.spItemId||null,
  };
}

// ── Inline styles ────────────────────────────────────────────────────────────
const S = {
  page:    { display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" },
  toolbar: { display:"flex", gap:8, padding:"10px 20px", background:"var(--s1)", borderBottom:"1px solid var(--b1)", flexWrap:"wrap", alignItems:"center", flexShrink:0 },
  search:  { position:"relative", flex:"1", minWidth:200, maxWidth:320 },
  sInput:  { width:"100%", background:"var(--s2)", border:"1px solid var(--b2)", color:"var(--tx)", borderRadius:"var(--r)", padding:"6px 10px 6px 30px", fontFamily:"var(--sans)", fontSize:12, outline:"none" },
  sel:     { background:"var(--s2)", border:"1px solid var(--b2)", color:"var(--tx)", borderRadius:"var(--r)", padding:"6px 10px", fontFamily:"var(--sans)", fontSize:12, outline:"none", cursor:"pointer", maxWidth:220 },
  tWrap:   { flex:1, overflowY:"auto" },
  table:   { width:"100%", borderCollapse:"collapse" },
  th:      { padding:"8px 14px", textAlign:"left", fontSize:10, fontWeight:600, color:"var(--tx3)", textTransform:"uppercase", letterSpacing:".05em", borderBottom:"1px solid var(--b1)", background:"var(--s1)", whiteSpace:"nowrap", cursor:"pointer", userSelect:"none", position:"sticky", top:0, zIndex:1 },
  td:      { padding:"9px 14px", fontSize:12, borderBottom:"1px solid var(--b1)", whiteSpace:"nowrap" },
  hn:      { fontFamily:"var(--mono)", fontSize:11, color:"var(--ima-blue)", fontWeight:500 },
  dim:     { color:"var(--tx2)" },
  sm:      { color:"var(--tx2)", fontSize:11 },
  dot:     (yes) => ({ display:"flex", alignItems:"center", justifyContent:"center", width:18, height:18, borderRadius:3, fontSize:9, fontWeight:700, background:yes?"rgba(106,191,75,.2)":"rgba(63,115,141,.1)", color:yes?"var(--ima-green)":"var(--tx3)" }),
  overlay: { position:"fixed", inset:0, background:"rgba(0,0,0,.65)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center", backdropFilter:"blur(4px)" },
  modal:   { background:"var(--s1)", border:"1px solid var(--b2)", borderRadius:12, width:700, maxWidth:"95vw", maxHeight:"90vh", overflowY:"auto" },
  mHead:   { padding:"18px 22px 14px", borderBottom:"1px solid var(--b1)", display:"flex", alignItems:"flex-start", justifyContent:"space-between" },
  mBody:   { padding:"18px 22px", display:"flex", flexDirection:"column", gap:16 },
  mSec:    { display:"flex", flexDirection:"column", gap:10 },
  mSH:     { fontSize:10, fontWeight:600, color:"var(--tx3)", textTransform:"uppercase", letterSpacing:".06em", paddingBottom:8, borderBottom:"1px solid var(--b1)", marginBottom:4 },
  mGrid:   { display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 },
  fg:      { display:"flex", flexDirection:"column", gap:3 },
  fl:      { fontSize:10, color:"var(--tx3)" },
  fv:      { fontSize:12, color:"var(--tx)" },
  fc:      { background:"var(--s2)", border:"1px solid var(--b2)", color:"var(--tx)", borderRadius:"var(--r)", padding:"7px 10px", fontFamily:"var(--sans)", fontSize:12, width:"100%", outline:"none" },
  mFoot:   { padding:"14px 22px", borderTop:"1px solid var(--b1)", display:"flex", gap:8, justifyContent:"flex-end", alignItems:"center" },
  sdot:    (color) => ({ display:"inline-flex", alignItems:"center", gap:5, padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:600, background:color+"18", color }),
};


// ── MultiSelect dropdown component ───────────────────────────────────────────
function MultiSelect({ label, value, onChange, options }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (v) => {
    const next = value.includes(v) ? value.filter(x => x !== v) : [...value, v];
    onChange(next);
  };

  const displayLabel = value.length === 0
    ? `All ${label}s`
    : value.length === 1
      ? (options.find(o => o.v === value[0])?.l || value[0])
      : `${label}: ${value.length}`;

  return (
    <div ref={ref} style={{ position:"relative", flexShrink:0 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: value.length ? "var(--ima-blue)" : "var(--s2)",
          border: `1px solid ${value.length ? "var(--ima-blue)" : "var(--b2)"}`,
          color: value.length ? "#000" : "var(--tx)",
          borderRadius:"var(--r)", padding:"6px 10px", fontFamily:"var(--sans)",
          fontSize:12, cursor:"pointer", whiteSpace:"nowrap", display:"flex",
          alignItems:"center", gap:5
        }}
      >
        {displayLabel}
        <span style={{ fontSize:9, opacity:0.7 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{
          position:"absolute", top:"calc(100% + 4px)", left:0, zIndex:50,
          background:"var(--s1)", border:"1px solid var(--b2)", borderRadius:"var(--r)",
          minWidth:180, maxHeight:260, overflowY:"auto",
          boxShadow:"0 4px 16px rgba(0,0,0,.4)"
        }}>
          {options.map(o => (
            <div key={o.v}
              onClick={() => toggle(o.v)}
              style={{
                padding:"7px 12px", cursor:"pointer", fontSize:12,
                display:"flex", alignItems:"center", gap:8,
                background: value.includes(o.v) ? "var(--s2)" : "transparent",
                color: value.includes(o.v) ? "var(--ima-blue)" : "var(--tx)",
              }}
              onMouseEnter={e => e.currentTarget.style.background="var(--s2)"}
              onMouseLeave={e => e.currentTarget.style.background=value.includes(o.v)?"var(--s2)":"transparent"}
            >
              <span style={{
                width:14, height:14, border:`1.5px solid ${value.includes(o.v)?"var(--ima-blue)":"var(--b2)"}`,
                borderRadius:3, display:"flex", alignItems:"center", justifyContent:"center",
                background: value.includes(o.v) ? "var(--ima-blue)" : "transparent",
                flexShrink:0, fontSize:9, color:"#000"
              }}>{value.includes(o.v) ? "✓" : ""}</span>
              {o.l}
            </div>
          ))}
          {value.length > 0 && (
            <div onClick={() => onChange([])}
              style={{ padding:"6px 12px", fontSize:11, color:"var(--tx3)", borderTop:"1px solid var(--b1)", cursor:"pointer" }}>
              Clear
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export function DevicesPage({ user }) {
  const [devices,    setDevices]    = useState([]);
  const [filtered,   setFiltered]   = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [loadMsg,    setLoadMsg]    = useState("");
  const [saveMsg,    setSaveMsg]    = useState(""); // success/error for save
  const [saveMsgOk,  setSaveMsgOk]  = useState(true);
  const [lastSync,   setLastSync]   = useState(localStorage.getItem("ima-device-sync")||"");
  const [sortField,  setSortField]  = useState("hostname");
  const [sortAsc,    setSortAsc]    = useState(true);
  const [q,          setQ]          = useState("");
  const [fStatus,    setFStatus]    = useState([]);
  const [fActivity,  setFActivity]  = useState([]);
  const [fLocation,  setFLocation]  = useState([]);
  const [fOrg,       setFOrg]       = useState([]);
  const [fCoverage,  setFCoverage]  = useState([]);
  const [fOS,        setFOS]        = useState([]);
  const [selected,   setSelected]   = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [editStatus, setEditStatus] = useState("");
  const [editOffice, setEditOffice] = useState("unknown");
  const [editUser,   setEditUser]   = useState("");
  const [editAsset,  setEditAsset]  = useState("");
  const [editNotes,  setEditNotes]  = useState("");

  // ── Filter + sort whenever inputs change ─────────────────────────────────────
  useEffect(() => {
    const ql = q.toLowerCase();
    let list = devices.filter(d => {
      if (ql && !`${d.hostname} ${d.user} ${d.serial} ${d.assetTag} ${d.location}`.toLowerCase().includes(ql)) return false;
      if (fStatus.length   && !fStatus.includes(d.deviceStatus))   return false;
      if (fActivity.length && !fActivity.includes(d.ninjaStatus))  return false;
      if (fLocation.length && !fLocation.includes(d.location))     return false;
      if (fOrg.length      && !fOrg.includes(d.locationOrg))       return false;
      if (fOS.length       && !fOS.some(o => (d.os||"").toLowerCase().includes(o.toLowerCase()))) return false;
      if (fCoverage.length) {
        const cv = fCoverage;
        const passes = (
          (!cv.includes("full")     || (d.inNinja && d.inIntune && d.inCS)) &&
          (!cv.includes("noCS")     || !d.inCS) &&
          (!cv.includes("noNinja")  || !d.inNinja) &&
          (!cv.includes("noIntune") || !d.inIntune)
        );
        if (!passes) return false;
      }
      return true;
    });
    list = [...list].sort((a, b) => {
      const av = (a[sortField] || "").toString().toLowerCase();
      const bv = (b[sortField] || "").toString().toLowerCase();
      if (av < bv) return sortAsc ? -1 : 1;
      if (av > bv) return sortAsc ? 1 : -1;
      return 0;
    });
    setFiltered(list);
  }, [devices, q, fStatus, fActivity, fLocation, fOrg, fCoverage, fOS, sortField, sortAsc]);

  const handleSort = (field) => {
    if (sortField === field) setSortAsc(a => !a);
    else { setSortField(field); setSortAsc(true); }
  };

  // ── Column resizing ───────────────────────────────────────────────────────
  const [colWidths, setColWidths] = React.useState({
    hostname:160, serial:120, user:150, location:200,
    deviceStatus:120, ninjaStatus:90, coverage:70,
    lastSeen:90, os:180, deviceCategory:80
  });
  const resizing = React.useRef(null);

  const startResize = (col, e) => {
    e.preventDefault();
    e.stopPropagation();
    resizing.current = { col, startX: e.clientX, startW: colWidths[col] };
    const onMove = (ev) => {
      const delta = ev.clientX - resizing.current.startX;
      setColWidths(prev => ({
        ...prev,
        [resizing.current.col]: Math.max(50, resizing.current.startW + delta)
      }));
    };
    const onUp = () => {
      resizing.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // Multi-select handler
  const handleMulti = (setter) => (e) => {
    const vals = Array.from(e.target.selectedOptions).map(o => o.value).filter(v => v !== "");
    setter(vals);
  };

  // ── Sync ──────────────────────────────────────────────────────────────────────
  const sync = async () => {
    setLoading(true); setSaveMsg("");
    try {
      setLoadMsg("Fetching NinjaRMM devices...");
      const nR = await fetch(`${FUNCTION_APP_URL}/api/ninja-devices?code=${FUNCTION_KEY}`);
      if (!nR.ok) throw new Error(`Ninja: ${nR.status} ${await nR.text()}`);
      const ninjaPayload = await nR.json();
      const ninjaDevices  = ninjaPayload.devices     || [];
      const ninjaOrgMap   = ninjaPayload.orgMap      || {};
      const ninjaLocMap   = ninjaPayload.locationMap || {};

      setLoadMsg(`${ninjaDevices.length} Ninja devices. Fetching Intune...`);
      const iR = await fetch(`${FUNCTION_APP_URL}/api/intune-devices?code=${FUNCTION_KEY}`);
      if (!iR.ok) throw new Error(`Intune: ${iR.status} ${await iR.text()}`);
      const intuneDevices = (await iR.json()).devices || [];

      setLoadMsg(`${intuneDevices.length} Intune devices. Fetching CrowdStrike...`);
      const cR = await fetch(`${FUNCTION_APP_URL}/api/cs-devices?code=${FUNCTION_KEY}`);
      if (!cR.ok) throw new Error(`CS: ${cR.status} ${await cR.text()}`);
      const csDevices = (await cR.json()).devices || [];

      setLoadMsg("Loading SharePoint overrides...");
      let overrides = {};
      try {
        overrides = await loadSPOverrides();
      } catch(spErr) {
        console.warn("SharePoint load failed (continuing without overrides):", spErr.message);
      }

      setLoadMsg("Reconciling...");
      const reconciled = reconcile(ninjaDevices, intuneDevices, csDevices, overrides, ninjaOrgMap, ninjaLocMap);
      setDevices(reconciled);

      const now = new Date().toLocaleString();
      setLastSync(now);
      localStorage.setItem("ima-device-sync", now);
    } catch (e) {
      setSaveMsg("Sync error: " + e.message); setSaveMsgOk(false);
    } finally {
      setLoading(false); setLoadMsg("");
    }
  };

  // ── Open device modal ─────────────────────────────────────────────────────────
  const openDevice = (d) => {
    setSelected(d);
    setEditStatus(d.deviceStatus || "Active");
    setEditOffice(d.officeId || "unknown");
    setEditUser(d.user || "");
    setEditAsset(d.assetTag || "");
    setEditNotes(d.notes || "");
    setSaveMsg("");
  };

  // ── Save to SharePoint ────────────────────────────────────────────────────────
  const saveDevice = async () => {
    if (!selected) return;
    setSaving(true); setSaveMsg("Saving...");
    try {
      const office = OFFICE_BY_ID[editOffice] || OFFICE_BY_ID["unknown"];
      const fields = {
        DeviceHostname: selected.hostname,
        SerialNumber:   selected.serial || "",
        DeviceStatus:   editStatus,
        OfficeID:       editOffice,
        LocationLabel:  office?.label || "",
        AssignedUser:   editUser,
        AssetTag:       editAsset,
        Notes:          editNotes,
        UpdatedAt:      new Date().toISOString(),
      };
      const newSpId = await saveSPItem(selected.hostname, fields, selected.spItemId);
      const updated = {
        ...selected,
        deviceStatus: editStatus,
        officeId:     editOffice,
        location:     office?.label || "Unknown",
        locationOrg:  office?.org || "",
        user:         editUser || selected.user,
        assetTag:     editAsset,
        notes:        editNotes,
        spItemId:     newSpId,
      };
      setDevices(prev => prev.map(d => d.hostname === selected.hostname ? updated : d));
      setSelected(updated);
      setSaveMsg("Saved to SharePoint"); setSaveMsgOk(true);
    } catch(e) {
      setSaveMsg("Save failed: " + e.message); setSaveMsgOk(false);
      console.error("saveDevice error:", e);
    } finally {
      setSaving(false);
    }
  };

  // ── Export ────────────────────────────────────────────────────────────────────
  const clearFilters = () => {
    setQ(""); setFStatus([]); setFActivity([]); setFLocation([]);
    setFOrg([]); setFCoverage([]); setFOS([]);
  };

  const exportCSV = () => {
    const cols = ["hostname","serial","deviceStatus","location","user","assetTag","ninjaStatus","lastSeen","os","inNinja","inIntune","inCS","compliance","csVersion","notes"];
    const csv  = [cols.join(","), ...filtered.map(d => cols.map(c => `"${(d[c]||"").toString().replace(/"/g,'""')}"`).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type:"text/csv" }));
    a.download = `IMA_Devices_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  // ── Computed ──────────────────────────────────────────────────────────────────
  const usedLocations = [...new Set(devices.map(d => d.location).filter(Boolean))].sort();
  // Stats reflect currently filtered set so numbers update as filters change
  const isFiltered = !!(q || fStatus.length || fActivity.length || fLocation.length || fOrg.length || fCoverage.length || fOS.length);
  const base = isFiltered ? filtered : devices;
  const wk   = base.filter(d => d.deviceCategory!=="Server" && d.deviceCategory!=="Linux");
  const allWk = devices.filter(d => d.deviceCategory!=="Server" && d.deviceCategory!=="Linux");
  const stats = [
    { label:"Total Devices",  value:`${base.length}${isFiltered ? " / "+devices.length : ""}`, color:"var(--ima-blue)",
      filterFn: isFiltered ? clearFilters : null, active: false },
    { label:"Deployed",       value:wk.filter(d=>d.deviceStatus==="Deployed").length,           color:"var(--ima-green)",
      filterFn:()=>{ clearFilters(); setFStatus(["Deployed"]); }, active: fStatus.includes("Deployed") },
    { label:"Activity Active",value:wk.filter(d=>d.ninjaStatus==="Active").length,              color:"var(--ima-green)", sub:"seen ≤30d",
      filterFn:()=>{ clearFilters(); setFActivity(["Active"]); }, active: fActivity.includes("Active") },
    { label:"Stale",          value:wk.filter(d=>d.ninjaStatus==="Stale").length,               color:"var(--ima-accent)", sub:"31-60d",
      filterFn:()=>{ clearFilters(); setFActivity(["Stale"]); }, active: fActivity.includes("Stale") },
    { label:"Offline",        value:wk.filter(d=>d.ninjaStatus==="Unknown").length,             color:"var(--tx3)", sub:">60d",
      filterFn:()=>{ clearFilters(); setFActivity(["Unknown"]); }, active: fActivity.includes("Unknown") },
    { label:"Maintenance",    value:wk.filter(d=>d.deviceStatus==="Under Maintenance").length,  color:"var(--ima-accent)",
      filterFn:()=>{ clearFilters(); setFStatus(["Under Maintenance"]); }, active: fStatus.includes("Under Maintenance") },
    { label:"No CS Sensor",   value:wk.filter(d=>!d.inCS&&d.ninjaStatus==="Active").length,    color:"var(--red)",
      filterFn:()=>{ clearFilters(); setFCoverage(["noCS"]); setFActivity(["Active"]); }, active: fCoverage.includes("noCS") },
    { label:"Servers",        value:base.filter(d=>d.deviceCategory==="Server"||d.deviceCategory==="Linux").length, color:"var(--tx2)",
      filterFn:()=>{ clearFilters(); setFOS(["Windows Server"]); }, active: false },
  ];

  const SC = {
    "Deployed":              "var(--ima-green)",
    "Ready for Deployment":  "var(--purple)",
    "Under Maintenance":     "var(--ima-accent)",
    "Spare / In Storage":    "#6b7280",
    "Retired":               "var(--tx3)",
    "Lost / Stolen":         "var(--red)",
  };
  // Activity = auto-calculated from last seen (not manually set)
  const NC = {
    Active:  "var(--ima-green)",   // seen within 30 days
    Stale:   "var(--ima-accent)",  // 31-60 days
    Unknown: "var(--tx3)",         // >60 days
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      {/* Stats */}
      <div style={{ display:"flex", gap:1, background:"var(--b1)", borderBottom:"1px solid var(--b1)", flexShrink:0 }}>
        {stats.map(s => (
          <div key={s.label}
            onClick={() => s.filterFn && s.filterFn()}
            style={{
              flex:1, background:"var(--s1)", padding:"10px 16px", minWidth:0,
              cursor: s.filterFn ? "pointer" : "default",
              borderBottom: s.active ? `2px solid ${s.color}` : "2px solid transparent",
              transition:"background .1s",
            }}
            onMouseEnter={e => s.filterFn && (e.currentTarget.style.background="var(--s2)")}
            onMouseLeave={e => s.filterFn && (e.currentTarget.style.background="var(--s1)")}>
            <div style={{ fontSize:9, color:"var(--tx3)", textTransform:"uppercase", letterSpacing:".05em", marginBottom:3, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{s.label}</div>
            <div style={{ fontSize:20, fontWeight:600, color:s.color, lineHeight:1 }}>{devices.length ? s.value : "—"}</div>
            {s.sub && <div style={{ fontSize:9, color:"var(--tx3)", marginTop:2 }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={S.toolbar}>
        <div style={S.search}>
          <span style={{ position:"absolute", left:9, top:"50%", transform:"translateY(-50%)", color:"var(--tx3)", fontSize:12 }}>⌕</span>
          <input
            style={S.sInput}
            placeholder="Search hostname, user, serial, asset tag..."
            value={q}
            onChange={e => setQ(e.target.value)}
          />
        </div>
        <MultiSelect label="IT Status" value={fStatus} onChange={setFStatus}
          options={DEVICE_STATUSES.map(s=>({v:s,l:s}))} />
        <MultiSelect label="Activity" value={fActivity} onChange={setFActivity}
          options={[{v:"Active",l:"Active (≤30d)"},{v:"Stale",l:"Stale (31-60d)"},{v:"Unknown",l:"Offline (>60d)"}]} />
        <MultiSelect label="Location" value={fLocation} onChange={setFLocation}
          options={usedLocations.map(l=>({v:l,l:l}))} />
        <MultiSelect label="Org" value={fOrg} onChange={setFOrg}
          options={[{v:"IMA",l:"IMA Gov Services"},{v:"CR",l:"Clinical Research"},{v:"Corporate",l:"Corporate"},{v:"PayerServices",l:"Payer Services"}]} />
        <MultiSelect label="Coverage" value={fCoverage} onChange={setFCoverage}
          options={[{v:"full",l:"Full (N+I+CS)"},{v:"noCS",l:"Missing CS"},{v:"noNinja",l:"Missing Ninja"},{v:"noIntune",l:"Missing Intune"}]} />
        <MultiSelect label="OS" value={fOS} onChange={setFOS}
          options={[{v:"Windows 11",l:"Win 11"},{v:"Windows 10",l:"Win 10"},{v:"Windows Server",l:"Server"},{v:"macOS",l:"macOS"},{v:"iOS",l:"iOS"},{v:"Android",l:"Android"}]} />
        <div style={{ marginLeft:"auto", display:"flex", gap:8, alignItems:"center" }}>
          {lastSync && <span style={{ fontSize:11, color:"var(--tx3)" }}>Synced {lastSync}</span>}
          {saveMsg && <span style={{ fontSize:11, color:saveMsgOk?"var(--ima-green)":"var(--red)" }}>{saveMsg}</span>}
          <span style={{ fontSize:11, color:"var(--tx2)", padding:"3px 10px", background:"var(--s2)", border:"1px solid var(--b1)", borderRadius:20 }}>{filtered.length} / {devices.length}</span>
          {(fStatus.length||fActivity.length||fLocation.length||fOrg.length||fCoverage.length||fOS.length||q) ?
            <Btn variant="ghost" small onClick={clearFilters}>✕ Clear</Btn> : null}
          <Btn variant="ghost" small onClick={exportCSV} disabled={!devices.length}>Export CSV</Btn>
          <Btn variant="primary" small onClick={sync} disabled={loading}>
            {loading ? <><Spinner size={10}/> {loadMsg||"Syncing..."}</> : "⟳ Sync Now"}
          </Btn>
        </div>
      </div>

      {/* Table */}
      <div style={S.tWrap}>
        {!devices.length && !loading ? (
          <div style={{ padding:60, textAlign:"center", color:"var(--tx3)" }}>
            <div style={{ fontSize:32, marginBottom:12 }}>🖥</div>
            <div style={{ fontSize:14, color:"var(--tx2)", marginBottom:8 }}>No devices loaded</div>
            <div style={{ fontSize:12, marginBottom:20 }}>Click Sync Now to pull live data from Ninja, Intune, and CrowdStrike</div>
            <Btn variant="primary" onClick={sync}>⟳ Sync Now</Btn>
          </div>
        ) : (
          <table style={{...S.table, tableLayout:'fixed'}}>
            <thead>
              <tr>
                {[
                  ["hostname","Hostname"], ["serial","Serial"], ["user","User"],
                  ["location","Location"], ["deviceStatus","IT Status"],
                  ["ninjaStatus","Activity"], [null,"N · I · CS"],
                  ["lastSeen","Last Seen"], ["os","OS"], ["deviceCategory","Type"]
                ].map(([f, l], i) => {
                  const ck = f || ["coverage","lastSeen","os","deviceCategory"][i-6] || l.toLowerCase().replace(/ /g,"");
                  const colKey = {
                    "Hostname":"hostname","Serial":"serial","User":"user",
                    "Location":"location","Status":"deviceStatus",
                    "Ninja Status":"ninjaStatus","N · I · CS":"coverage",
                    "Last Seen":"lastSeen","OS":"os","Type":"deviceCategory"
                  }[l] || l.toLowerCase();
                  return (
                    <th key={l} style={{
                      ...S.th,
                      width: colWidths[colKey],
                      minWidth: colWidths[colKey],
                      maxWidth: colWidths[colKey],
                      color: sortField===f?"var(--ima-blue)":"var(--tx3)",
                      position:"relative", overflow:"hidden",
                      userSelect:"none",
                    }}
                      onClick={() => f && handleSort(f)}>
                      <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", display:"block", paddingRight:10 }}>
                        {l}{f && sortField===f ? (sortAsc?" ↑":" ↓") : ""}
                      </span>
                      <span
                        onMouseDown={(e) => startResize(colKey, e)}
                        onClick={e => e.stopPropagation()}
                        style={{
                          position:"absolute", right:0, top:0, bottom:0, width:6,
                          cursor:"col-resize", background:"transparent",
                          borderRight:"2px solid transparent",
                          transition:"border-color .15s",
                        }}
                        onMouseEnter={e => e.currentTarget.style.borderRightColor="var(--ima-blue)"}
                        onMouseLeave={e => e.currentTarget.style.borderRightColor="transparent"}
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filtered.map((d, idx) => (
                <tr key={idx}
                  onClick={() => openDevice(d)}
                  style={{ cursor:"pointer", borderBottom:"1px solid var(--b1)" }}
                  onMouseEnter={e => e.currentTarget.style.background="var(--s2)"}
                  onMouseLeave={e => e.currentTarget.style.background=""}>
                  <td style={{...S.td,...S.hn,    width:colWidths.hostname,     overflow:"hidden",textOverflow:"ellipsis"}} title={d.hostname}>{d.hostname}</td>
                  <td style={{...S.td,...S.sm,    width:colWidths.serial,       overflow:"hidden",textOverflow:"ellipsis", fontFamily:"var(--mono)"}} title={d.serial}>{d.serial}</td>
                  <td style={{...S.td,...S.dim,   width:colWidths.user,         overflow:"hidden",textOverflow:"ellipsis"}} title={d.user}>{d.user}</td>
                  <td style={{...S.td,...S.sm,    width:colWidths.location,     overflow:"hidden",textOverflow:"ellipsis"}} title={d.location}>{d.location}</td>
                  <td style={{...S.td,            width:colWidths.deviceStatus, overflow:"hidden"}}><span style={S.sdot(SC[d.deviceStatus]||"var(--tx3)")}>{d.deviceStatus}</span></td>
                  <td style={{...S.td,            width:colWidths.ninjaStatus,  overflow:"hidden"}}><span style={S.sdot(NC[d.ninjaStatus]||"var(--tx3)")}>{d.ninjaStatus}</span></td>
                  <td style={{...S.td,            width:colWidths.coverage}}>
                    <div style={{ display:"flex", gap:3 }}>
                      <div style={S.dot(d.inNinja)} title="NinjaRMM">N</div>
                      <div style={S.dot(d.inIntune)} title="Intune">I</div>
                      <div style={S.dot(d.inCS)} title="CrowdStrike">C</div>
                    </div>
                  </td>
                  <td style={{...S.td,...S.sm,    width:colWidths.lastSeen,     overflow:"hidden"}}>{d.lastSeen}</td>
                  <td style={{...S.td,...S.sm,    width:colWidths.os,           overflow:"hidden",textOverflow:"ellipsis"}} title={d.os}>{(d.os||"").replace("Windows 10.0.","Win ")}</td>
                  <td style={{...S.td,...S.sm,    width:colWidths.deviceCategory,overflow:"hidden"}}>{d.deviceCategory||""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit modal */}
      {selected && (
        <div style={S.overlay} onClick={e => e.target===e.currentTarget && setSelected(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.mHead}>
              <div>
                <div style={{ fontFamily:"var(--mono)", fontSize:15, color:"var(--ima-blue)", fontWeight:600 }}>{selected.hostname}</div>
                <div style={{ fontSize:11, color:"var(--tx3)", marginTop:2 }}>
                  Serial: {selected.serial||"Unknown"}{selected.assetTag ? ` · Asset: ${selected.assetTag}` : ""}
                </div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background:"none", border:"none", color:"var(--tx3)", cursor:"pointer", fontSize:20, lineHeight:1 }}>×</button>
            </div>
            <div style={S.mBody}>
              {/* API data — read only */}
              <div style={S.mSec}>
                <div style={S.mSH}>API Data — Read Only</div>
                <div style={S.mGrid}>
                  {[
                    ["OS",                selected.os||"—"],
                    ["Hardware",          [selected.mfr,selected.model].filter(Boolean).join(" ")||"—"],
                    ["IP Address",        selected.ip||"—"],
                    ["Last Seen",         selected.lastSeen||"—"],
                    ["Intune Compliance", selected.compliance||"—"],
                    ["CS Sensor Version", selected.csVersion||"—"],
                  ].map(([l,v]) => (
                    <div key={l} style={S.fg}>
                      <div style={S.fl}>{l}</div>
                      <div style={{...S.fv, fontFamily:["IP Address","CS Sensor Version"].includes(l)?"var(--mono)":"inherit", fontSize:11}}>{v}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display:"flex", gap:8, marginTop:6 }}>
                  {[["NinjaRMM",selected.inNinja],["Intune",selected.inIntune],["CrowdStrike",selected.inCS]].map(([l,v]) => (
                    <span key={l} style={{ fontSize:11, padding:"2px 8px", borderRadius:20, background:v?"rgba(106,191,75,.15)":"rgba(224,82,82,.15)", color:v?"var(--ima-green)":"var(--red)" }}>
                      {v?"✓":"✗"} {l}
                    </span>
                  ))}
                </div>
              </div>

              {/* Editable fields */}
              <div style={S.mSec}>
                <div style={S.mSH}>Managed Fields — Saves to SharePoint</div>
                <div style={S.mGrid}>
                  <div style={S.fg}>
                    <label style={S.fl}>Device Status</label>
                    <select style={S.fc} value={editStatus} onChange={e => setEditStatus(e.target.value)}>
                      {DEVICE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div style={S.fg}>
                    <label style={S.fl}>Office Location</label>
                    <select style={S.fc} value={editOffice} onChange={e => setEditOffice(e.target.value)}>
                      {OFFICES.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  </div>
                  <div style={S.fg}>
                    <label style={S.fl}>Assigned User Override</label>
                    <input style={S.fc} value={editUser} onChange={e => setEditUser(e.target.value)} placeholder="user@theimagroup.com" />
                  </div>
                  <div style={S.fg}>
                    <label style={S.fl}>Asset Tag</label>
                    <input style={S.fc} value={editAsset} onChange={e => setEditAsset(e.target.value)} placeholder="IMA-XXXXXX" />
                  </div>
                </div>
                <div style={S.fg} style={{ marginTop:8 }}>
                  <label style={S.fl}>Notes</label>
                  <textarea style={{...S.fc, resize:"vertical", minHeight:70}} value={editNotes} onChange={e => setEditNotes(e.target.value)} placeholder="Ticket #, issue description, warranty info..." />
                </div>
              </div>
            </div>
            <div style={S.mFoot}>
              {saveMsg && <span style={{ marginRight:"auto", fontSize:11, color:saveMsgOk?"var(--ima-green)":"var(--red)" }}>{saveMsg}</span>}
              <Btn variant="ghost" small onClick={() => setSelected(null)}>Cancel</Btn>
              <Btn variant="primary" small onClick={saveDevice} disabled={saving}>
                {saving ? <><Spinner size={10}/> Saving…</> : "Save Changes"}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
